<template>
  <div>
    <!-- Section: Basic Info -->
    <div class="form-section mb-6">
      <div class="section-header d-flex align-center mb-4">
        <v-icon color="indigo" class="me-2">mdi-information-outline</v-icon>
        <h3 class="text-subtitle-1 font-weight-bold indigo--text text--darken-2 mb-0">
          פרטים בסיסיים
        </h3>
      </div>

      <v-card outlined rounded="lg" class="section-card pa-5">
        <v-row>
          <!-- Gift Name -->
          <v-col cols="12" sm="6">
            <v-text-field
              outlined
              v-model="item.gift_name"
              prepend-inner-icon="mdi-gift-outline"
              :rules="[(v) => !!v || 'חובה']"
              rounded
              dense
              hide-details="auto"
            >
              <template slot="label">
                <strong class="red--text">*</strong>
                שם המתנה
              </template>
            </v-text-field>
          </v-col>

          <!-- Value to Get Gift -->
          <v-col cols="12" sm="6">
            <v-text-field
              outlined
              type="number"
              v-model.number="item.value_to_get_gift"
              prepend-inner-icon="mdi-currency-ils"
              :rules="[(v) => !!v || 'חובה']"
              rounded
              dense
              hide-details="auto"
            >
              <template slot="label">
                <strong class="red--text">*</strong>
                סכום מינימלי לקבלת מתנה
              </template>
            </v-text-field>
          </v-col>

          <!-- Times -->
          <v-col cols="12" sm="6">
            <v-text-field
              outlined
              type="number"
              v-model.number="item.times"
              prepend-inner-icon="mdi-repeat"
              :rules="[(v) => v > 0 || 'חובה']"
              rounded
              dense
              hide-details="auto"
            >
              <template slot="label">
                <strong class="red--text">*</strong>
                מספר פעמים
              </template>
            </v-text-field>
          </v-col>

          <!-- Status -->
          <v-col cols="12" sm="6" class="d-flex align-center">
            <div 
              class="status-toggle-card"
              :class="{ 'status-toggle-card--active': item.status }"
              @click="item.status = !item.status"
            >
              <div class="status-toggle-content">
                <div class="status-icon-wrapper" :class="{ 'status-icon-wrapper--active': item.status }">
                  <v-icon :color="item.status ? 'white' : 'grey darken-1'">
                    {{ item.status ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                </div>
                <div class="status-text">
                  <div class="status-label">סטטוס</div>
                  <div class="status-value" :class="{ 'status-value--active': item.status }">
                    {{ item.status ? 'פעיל' : 'לא פעיל' }}
                  </div>
                </div>
              </div>
              <div class="status-toggle-switch">
                <div class="toggle-track" :class="{ 'toggle-track--active': item.status }">
                  <div class="toggle-thumb" :class="{ 'toggle-thumb--active': item.status }"></div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>
    </div>

    <!-- Section: Campaign Type -->
    <div class="form-section mb-6">
      <div class="section-header d-flex align-center mb-4">
        <v-icon color="indigo" class="me-2">mdi-tag-multiple</v-icon>
        <h3 class="text-subtitle-1 font-weight-bold indigo--text text--darken-2 mb-0">
          סוג המבצע
        </h3>
      </div>

      <v-card outlined rounded="lg" class="section-card pa-5">
        <v-row>
          <v-col cols="12">
            <div class="type-selector">
              <div
                class="type-option"
                :class="{ 'type-option--active': item.type === 'cart_total' }"
                @click="selectType('cart_total')"
              >
                <v-icon
                  :color="item.type === 'cart_total' ? 'white' : 'blue darken-1'"
                  large
                  class="mb-2"
                >mdi-cart</v-icon>
                <div class="type-label">סכום עגלה</div>
                <div class="type-desc text-caption">מתנה לפי סכום עגלה כוללת</div>
              </div>
              <div
                class="type-option"
                :class="{ 'type-option--active': item.type === 'brand' }"
                @click="selectType('brand')"
              >
                <v-icon
                  :color="item.type === 'brand' ? 'white' : 'purple'"
                  large
                  class="mb-2"
                >mdi-tag-multiple</v-icon>
                <div class="type-label">לפי מותג</div>
                <div class="type-desc text-caption">מתנה לפי רכישת מותג ספציפי</div>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Single Brand Selector (only for type=brand) -->
        <v-expand-transition>
          <v-row v-if="item.type === 'brand'">
            <v-col cols="12">
              <div class="brand-selector-wrapper mt-2">
                <h4 class="text-body-2 font-weight-bold mb-3">
                  <v-icon small color="purple" class="me-1">mdi-store</v-icon>
                  בחירת מותג במבצע
                </h4>
                <v-autocomplete
                  v-model="item.brand_id"
                  :items="brands_list"
                  item-value="id"
                  item-text="name"
                  label="בחר מותג"
                  outlined
                  rounded
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-store"
                  :rules="[(v) => (item.type === 'brand' ? !!v : true) || 'חובה לבחור מותג']"
                >
                  <template v-slot:item="{ item: brand }">
                    <v-list-item-avatar v-if="brand.image" size="32">
                      <img :src="`${$store.state.def_path}/${brand.image}`" />
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ brand.name }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </div>
            </v-col>
          </v-row>
        </v-expand-transition>
      </v-card>
    </div>

    <!-- Section: Gift Image -->
    <div class="form-section mb-6">
      <div class="section-header d-flex align-center mb-4">
        <v-icon color="indigo" class="me-2">mdi-image-outline</v-icon>
        <h3 class="text-subtitle-1 font-weight-bold indigo--text text--darken-2 mb-0">
          תמונת מתנה
        </h3>
      </div>

      <v-card outlined rounded="lg" class="section-card pa-5">
        <v-row>
          <v-col cols="12" sm="6">
            <v-file-input
              prepend-icon=""
              prepend-inner-icon="mdi-image"
              outlined
              rounded
              dense
              label="בחירת תמונה"
              v-model="gift_image_file"
              accept="image/*"
              hide-details="auto"
              @change="onImageChange"
            ></v-file-input>
          </v-col>
          <v-col cols="12" sm="6">
            <div class="image-preview-wrapper" v-if="imagePreview">
              <v-img
                :src="imagePreview"
                max-height="120"
                max-width="200"
                contain
                class="rounded-lg"
              ></v-img>
            </div>
            <div class="image-placeholder" v-else>
              <v-icon large color="grey lighten-1">mdi-image-plus</v-icon>
              <p class="text-caption grey--text mb-0 mt-2">אין תמונה</p>
            </div>
          </v-col>
        </v-row>
      </v-card>
    </div>

    <!-- Section: Dates -->
    <div class="form-section mb-6">
      <div class="section-header d-flex align-center mb-4">
        <v-icon color="indigo" class="me-2">mdi-calendar-range</v-icon>
        <h3 class="text-subtitle-1 font-weight-bold indigo--text text--darken-2 mb-0">
          תאריכים
        </h3>
      </div>

      <v-card outlined rounded="lg" class="section-card pa-5">
        <v-row>
          <!-- Start Date -->
          <v-col cols="12" sm="6">
            <v-menu
              v-model="menu_1"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="item.start_date"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  :rules="[(v) => !!v || 'חובה']"
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  rounded
                  dense
                  hide-details="auto"
                >
                  <template slot="label">
                    <strong class="red--text">*</strong>
                    תאריך התחלה
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="item.start_date"
                @input="menu_1 = false"
                locale="he"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <!-- End Date -->
          <v-col cols="12" sm="6">
            <v-menu
              v-model="menu_2"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="item.end_date"
                  prepend-inner-icon="mdi-calendar-check"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  :rules="[
                    () => !!compare_dates || 'נא לסמן תאריך אחרי תאריך התחלה',
                    (v) => !!v || 'חובה',
                  ]"
                  rounded
                  dense
                  hide-details="auto"
                >
                  <template slot="label">
                    <strong class="red--text">*</strong>
                    עד תאריך
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="item.end_date"
                @input="menu_2 = false"
                locale="he"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
      </v-card>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    item: Object,
    compare_dates: Boolean,
  },
  data() {
    return {
      menu_1: false,
      menu_2: false,
      brands_list: [],
      gift_image_file: null,
      imagePreview: null,
    };
  },
  watch: {
    'item.gift_image': {
      immediate: true,
      handler(newVal) {
        if (newVal && !this.gift_image_file) {
          this.imagePreview = `${this.$store.state.def_path}/${newVal}`;
        }
      }
    }
  },
  mounted() {
    this.get_brands();
  },
  methods: {
    get_brands() {
      this.$store
        .dispatch("public__request", {
          config: {
            url: "brands",
            method: "get",
          },
        })
        .then((res) => {
          this.brands_list = (res.data && res.data.data) || [];
        });
    },
    selectType(type) {
      this.item.type = type;
      if (type === 'cart_total') {
        this.item.brand_id = null;
      }
    },
    onImageChange(file) {
      if (file) {
        // Create preview URL for the selected file
        this.imagePreview = URL.createObjectURL(file);
        this.$emit('image-selected', file);
      } else {
        this.imagePreview = this.item.gift_image ? `${this.$store.state.def_path}/${this.item.gift_image}` : null;
        this.$emit('image-selected', null);
      }
    },
    clearImage() {
      this.gift_image_file = null;
      this.imagePreview = null;
      this.$emit('image-selected', null);
    },
  },
};
</script>

<style lang="scss" scoped>
.section-header {
  border-bottom: 2px solid #e8eaf6;
  padding-bottom: 8px;
}

.section-card {
  border-color: #e8eaf6 !important;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 2px 12px rgba(57, 73, 171, 0.08) !important;
  }
}

.type-selector {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.type-option {
  flex: 1;
  min-width: 160px;
  padding: 24px 16px;
  text-align: center;
  border: 2px solid #e8eaf6;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fafbff;

  &:hover {
    border-color: #9fa8da;
    background: #f0f2ff;
  }

  &--active {
    background: linear-gradient(135deg, #3949ab, #5c6bc0);
    border-color: #3949ab;
    color: white;

    .type-label {
      color: white;
      font-weight: 700;
    }

    .type-desc {
      color: rgba(255, 255, 255, 0.8) !important;
    }
  }
}

.type-label {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.type-desc {
  color: #999;
}

.brand-selector-wrapper {
  border: 1px dashed #d1c4e9;
  border-radius: 12px;
  padding: 16px;
  background: #faf5ff;
}

.image-preview-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: #f5f7ff;
  border-radius: 12px;
  border: 1px solid #e8eaf6;
}

.image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #fafbff;
  border-radius: 12px;
  border: 2px dashed #e8eaf6;
  min-height: 120px;
}

.status-switch-wrapper {
  background: #f5f7ff;
  border-radius: 12px;
  padding: 8px 16px;
  border: 1px solid #e8eaf6;
}

// Modern Status Toggle Card
.status-toggle-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%);
  border: 2px solid #e8eaf6;
  border-radius: 16px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(57, 73, 171, 0.06);

  &:hover {
    border-color: #9fa8da;
    box-shadow: 0 4px 16px rgba(57, 73, 171, 0.12);
    transform: translateY(-1px);
  }

  &--active {
    background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
    border-color: #4caf50;
    box-shadow: 0 4px 16px rgba(76, 175, 80, 0.15);
  }
}

.status-toggle-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #e8eaf6;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &--active {
    background: linear-gradient(135deg, #4caf50, #66bb6a);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  }
}

.status-text {
  display: flex;
  flex-direction: column;
}

.status-label {
  font-size: 0.75rem;
  color: #9e9e9e;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.status-value {
  font-size: 1rem;
  font-weight: 700;
  color: #757575;
  transition: color 0.3s ease;

  &--active {
    color: #2e7d32;
  }
}

// Custom Toggle Switch
.status-toggle-switch {
  display: flex;
  align-items: center;
}

.toggle-track {
  width: 52px;
  height: 28px;
  background: #e0e0e0;
  border-radius: 14px;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);

  &--active {
    background: linear-gradient(135deg, #4caf50, #66bb6a);
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(76, 175, 80, 0.3);
  }
}

.toggle-thumb {
  width: 24px;
  height: 24px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  right: 2px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &--active {
    right: 26px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
}
</style>
